import type { StorageAdapter } from './storage/storage-adapter';
import type {
  ShoppingListItem,
  CreateShoppingListItemInput,
  UpdateShoppingListItemInput,
  ShoppingCategory,
} from '@/types/shopping-list';
import type { Task } from '@/types/task';
import { TaskCategory } from '@/types/task';
import { recordPendingDelete } from './sync-service';

export interface ShoppingListSuggestion {
  name: string;
  category: ShoppingCategory;
}

/** Suggestion map: [category, action] → suggested items */
const SUGGESTIONS: Partial<Record<string, Partial<Record<string, ShoppingListSuggestion[]>>>> = {
  [TaskCategory.FoodSource]: {
    plant: [
      { name: 'Seed mix', category: 'seed' },
      { name: 'Fertilizer', category: 'fertilizer' },
    ],
    soil_test: [{ name: 'Soil test kit', category: 'equipment' }],
    fertilize: [
      { name: 'Fertilizer', category: 'fertilizer' },
      { name: 'Spreader', category: 'equipment' },
    ],
    frost_seed: [{ name: 'Clover seed', category: 'seed' }],
  },
  [TaskCategory.StandPrep]: {
    hang: [
      { name: 'Ratchet straps', category: 'equipment' },
      { name: 'Steps/sticks', category: 'equipment' },
    ],
  },
  [TaskCategory.TrailCameras]: {
    swap_battery: [
      { name: 'Batteries', category: 'equipment' },
      { name: 'SD cards', category: 'equipment' },
    ],
  },
  [TaskCategory.GearEquipment]: {
    zero: [{ name: 'Ammunition', category: 'ammunition' }],
  },
  [TaskCategory.MineralNutrition]: {
    refresh: [{ name: 'Mineral block/mix', category: 'minerals' }],
  },
};

export class ShoppingListService {
  constructor(private readonly adapter: StorageAdapter) {}

  async getShoppingList(propertyId?: string): Promise<ShoppingListItem[]> {
    return this.adapter.getShoppingList(propertyId);
  }

  async addItem(data: CreateShoppingListItemInput): Promise<ShoppingListItem> {
    return this.adapter.createShoppingListItem(data);
  }

  async updateItem(id: string, data: UpdateShoppingListItemInput): Promise<ShoppingListItem> {
    return this.adapter.updateShoppingListItem(id, data);
  }

  async togglePurchased(id: string): Promise<ShoppingListItem> {
    const all = await this.adapter.getShoppingList();
    const item = all.find((i) => i.id === id);
    if (!item) throw new Error(`ShoppingListItem not found: ${id}`);
    const newPurchased = !item.purchased;
    const now = new Date().toISOString();
    return this.adapter.updateShoppingListItem(id, {
      purchased: newPurchased,
      purchasedAt: newPurchased ? now : undefined,
    });
  }

  async deleteItem(id: string): Promise<void> {
    await this.adapter.deleteShoppingListItem(id);
    await recordPendingDelete(id, 'shoppingListItem');
  }

  async getItemsByCategory(category: string): Promise<ShoppingListItem[]> {
    const all = await this.adapter.getShoppingList();
    return all.filter((item) => item.category === category);
  }

  async getUnpurchasedCount(): Promise<number> {
    const all = await this.adapter.getShoppingList();
    return all.filter((item) => !item.purchased).length;
  }

  /** Returns pre-defined purchase suggestions based on task category + action. */
  suggestItemsForTask(task: Task): ShoppingListSuggestion[] {
    const catMap = task.category ? SUGGESTIONS[task.category] : undefined;
    if (!catMap) return [];
    const action = (task.selectedAction ?? '').trim();
    return catMap[action] ?? [];
  }
}
